import { FC } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import type { CapturedPhoto } from '../MobileProductCapture.types.ts';
import { StepNav } from './StepNav';

type PhotoSelectStepProps = {
    photos: CapturedPhoto[];
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
    onSelectAll: () => void;
    onClear: () => void;
    onBack: () => void;
    onNext: () => void;
};

export const PhotoSelectStep: FC<PhotoSelectStepProps> = ({
    photos,
    selectedIds,
    onToggle,
    onSelectAll,
    onClear,
    onBack,
    onNext,
}) => {
    const { t } = useTranslation();
    const selectedCount = selectedIds.size;

    return (
        <div className="mpc-step mpc-step--select">
            <header className="mpc-step__header">
                <h2>{t('mobileProductCapture.select.title')}</h2>
                <p>{t('mobileProductCapture.select.subtitle')}</p>
            </header>

            <div className="mpc-select-toolbar">
                <span>{t('mobileProductCapture.select.selected', { count: selectedCount })}</span>
                <div>
                    <button type="button" className="mpc-text-btn" onClick={onSelectAll}>
                        {t('mobileProductCapture.select.selectAll')}
                    </button>
                    <button type="button" className="mpc-text-btn" onClick={onClear}>
                        {t('mobileProductCapture.select.clear')}
                    </button>
                </div>
            </div>

            <ul className="mpc-select-grid mpc-select-grid--fill">
                {photos.map((photo) => {
                    const selected = selectedIds.has(photo.id);
                    return (
                        <li key={photo.id}>
                            <button
                                type="button"
                                className={`mpc-select-tile${selected ? ' is-selected' : ''}`}
                                onClick={() => onToggle(photo.id)}
                                aria-pressed={selected}
                            >
                                <img src={photo.preview} alt={photo.name} />
                                <span className="mpc-select-tile__check">
                                    {selected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>

            {selectedCount === 0 && (
                <p className="mpc-hint mpc-hint--warn">{t('mobileProductCapture.select.needSelection')}</p>
            )}

            <StepNav
                onBack={onBack}
                onNext={onNext}
                nextDisabled={selectedCount === 0}
                nextLabel={t('mobileProductCapture.select.save')}
            />
        </div>
    );
};
